import React from "react";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";

// Search + category-drilldown + product catalog list for the create/edit
// enquiry form. Purely presentational — all filtering/selection state and
// the add-to-cart action live in the parent (Enquiries) and are passed in.
const ProductCatalogPanel = ({
  searchQuery,
  setSearchQuery,
  getFilteredProducts,
  getCategoryLevels,
  selectedCategoryPath,
  setSelectedCategoryPath,
  setAllProductCombinations,
  productCatalogRef,
  enquiryForm,
  addProductToCart,
  calculateSellingPrice,
}) => {
  return (
    <>
      <div className="search-and-category-section">
        <div className="search-filter compact">
          <label htmlFor="product-search">Search:</label>
          <div className="search-input-wrapper">
            <input
              id="product-search"
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <ButtonWithIcon
                icon="mdi:close"
                text=""
                onClick={() => setSearchQuery("")}
                className="clear-search-btn"
                type="button"
                title="Clear search"
              />
            )}
          </div>
          {searchQuery && (
            <small className="search-results-count">
              {getFilteredProducts().length} result(s)
            </small>
          )}
        </div>

        <div className="category-filter compact">
          <label>Category:</label>
          {getCategoryLevels().map((levelCategories, depth) => (
            <div
              key={depth}
              className={
                depth === 0
                  ? "category-buttons-inline"
                  : "subcategory-buttons-inline"
              }
            >
              {depth === 0 && (
                <button
                  type="button"
                  className={`category-btn ${
                    selectedCategoryPath.length === 0 ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedCategoryPath([]);
                    setAllProductCombinations([]);
                  }}
                >
                  All
                </button>
              )}
              {levelCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`category-btn ${depth > 0 ? "subcategory" : ""} ${
                    selectedCategoryPath[depth] === cat.id ? "active" : ""
                  }`}
                  onClick={() => {
                    // Keep the path up to this depth, replace
                    // (or add) the selection at this depth, and
                    // drop anything deeper that no longer
                    // applies.
                    setSelectedCategoryPath((prev) => [
                      ...prev.slice(0, depth),
                      cat.id,
                    ]);
                    if (depth > 0) {
                      setTimeout(() => {
                        productCatalogRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 300);
                    }
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="product-catalog" ref={productCatalogRef}>
        {getFilteredProducts().map((product) => (
          <div key={product.productId} className="product-card">
            <div className="product-card-header">
              <h4>{product.productName}</h4>
              <span className="combination-count">
                {product.combinations?.length || 0} variants
              </span>
            </div>

            <div className="combinations-grid">
              {product.combinations?.map((combination) => {
                const existingProduct = enquiryForm.products.find(
                  (p) =>
                    p.productId === product.productId &&
                    p.combinationId === combination.combinationId,
                );
                const existingQuantity = existingProduct?.quantity || 0;

                return (
                  <div
                    key={combination.combinationId}
                    className="combination-item compact"
                  >
                    <div className="combination-name">
                      <strong>Combination:</strong>{" "}
                      {combination.combinationName || "-"}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "2px",
                      }}
                    >
                      <strong>SKU:</strong> {combination.skuCode || "-"}
                    </div>
                    <div className="combination-specs">
                      {combination.specificationValues.map((spec, idx) => (
                        <span key={idx} className="spec-badge">
                          {spec.specificationName}: {spec.value}
                        </span>
                      ))}
                    </div>

                    <div className="combination-right">
                      <div className="price-info">
                        <div className="price-breakdown-tooltip">
                          <span className="selling-price">
                            ₹
                            {calculateSellingPrice(
                              combination.costPrice,
                              combination.marginPercentage,
                            ).toFixed(0)}
                          </span>
                          {combination.marginPercentage > 0 && (
                            <span className="margin-tag">
                              +{combination.marginPercentage}%
                            </span>
                          )}
                        </div>
                        {combination.discountPrice != null && (
                          <span
                            className="min-floor-price"
                            title="Maximum discount floor"
                          >
                            ↓₹
                            {combination.discountPrice.toFixed(0)}
                          </span>
                        )}
                      </div>

                      <button
                        className="icon-add-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          addProductToCart(product.productId, combination);
                        }}
                        disabled={!combination.available}
                        title="Add to cart"
                      >
                        +
                      </button>
                    </div>

                    {existingQuantity > 0 && (
                      <span className="quantity-dot">{existingQuantity}</span>
                    )}

                    {!combination.available && (
                      <span className="unavailable-badge">NA</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {getFilteredProducts().length === 0 && (
          <div className="no-products">
            <p className="help-text">
              {selectedCategoryPath.length === 0
                ? "Please select a category to view available products."
                : searchQuery
                  ? "No products found matching your search."
                  : "No products found."}
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default ProductCatalogPanel;
